import { AlertTriangle, Info } from 'lucide-react';
import { ReportSection } from '@/components/report/ReportSection';
import { cn } from '@/lib/utils';
import { TableFrame, Th, Td } from './TableFrame';
import { priorityTag } from './labels';
import type { DeliveryReport, ReviewRow } from './types';

interface ReviewTabProps {
  report: DeliveryReport;
}

/** High priority first, keeping the skill's order within each priority. */
function byPriority(rows: ReviewRow[]): ReviewRow[] {
  return [...rows.filter((row) => row[0] === 'alta'), ...rows.filter((row) => row[0] !== 'alta')];
}

export function ReviewTab({ report }: ReviewTabProps) {
  const review = byPriority(report.review);

  return (
    <div className="space-y-4">
      <ReportSection title="Puntos a revisar" count={review.length} subtitle="detectados en los datos">
        {review.length === 0 ? (
          <p className="text-adv-body-s text-adv-tertiary py-2">Nada detectado.</p>
        ) : (
          <ul className="space-y-2">
            {review.map(([priority, area, finding, impact], index) => (
              <ReviewItem
                key={`${area}-${index}`}
                priority={priority}
                area={area}
                finding={finding}
                impact={impact}
              />
            ))}
          </ul>
        )}
      </ReportSection>

      <ReportSection title="Cambios clave" subtitle="historial DV360 (GCS)" count={report.changes.length}>
        {report.changes.length === 0 ? (
          <p className="text-adv-body-s text-adv-tertiary py-2">Sin cambios relevantes en el historial.</p>
        ) : (
          <TableFrame
            head={
              <>
                <Th>Fecha</Th>
                <Th>Quién</Th>
                <Th>Línea</Th>
                <Th>Cambio</Th>
              </>
            }
          >
            {report.changes.map(([when, who, line, change], index) => (
              <tr key={`${when}-${index}`} className="hover:bg-adv-sunken">
                <Td className="adv-num whitespace-nowrap">{when}</Td>
                <Td className="adv-num whitespace-nowrap">{who}</Td>
                <Td className="min-w-[160px]">{line}</Td>
                <Td className="min-w-[220px]">{change}</Td>
              </tr>
            ))}
          </TableFrame>
        )}
      </ReportSection>
    </div>
  );
}

interface ReviewItemProps {
  priority: string;
  area: string;
  finding: string;
  impact: string;
}

function ReviewItem({ priority, area, finding, impact }: ReviewItemProps) {
  const isHigh = priority === 'alta';
  const tag = priorityTag(priority);
  const Icon = isHigh ? AlertTriangle : Info;

  return (
    <li
      className={cn(
        'flex items-start gap-3 text-adv-body-s',
        isHigh ? 'adv-alert p-3' : 'bg-adv-sunken rounded-adv-lg p-3 text-adv-primary'
      )}
    >
      <Icon size={16} className="flex-shrink-0 mt-0.5" />
      <div className="min-w-0 flex-1">
        <p className="flex flex-wrap items-center gap-2">
          <span className={`adv-tag ${tag.className}`}>{tag.label}</span>
          <span className="font-bold">{area}</span>
        </p>
        <p className="mt-1">{finding}</p>
        {impact && <p className="mt-1 opacity-80">{impact}</p>}
      </div>
    </li>
  );
}
